import * as React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';

export function orgLogoUrl(org, variant) {
  return `${process.env.REACT_APP_MGO_API_URL}/org/${org}/logo/${variant}`;
}

// Cache: "{org}/{mode}" -> index of the URL that successfully loaded.
// Survives across mounts so the logo doesn't blink on navigation.
const logoCache = {};

const OrgLogo = ({ org, alt = 'Logo', className, style, sx, ...rest }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const urls = React.useMemo(() => [
    orgLogoUrl(org, mode),
    orgLogoUrl(org, 'original'),
    `/orgs/${org}.png`,
  ], [org, mode]);

  const cacheKey = `${org}/${mode}`;
  const cachedStep = logoCache[cacheKey] ?? 0;
  const [step, setStep] = React.useState(cachedStep);

  React.useEffect(() => {
    setStep(logoCache[cacheKey] ?? 0);
  }, [cacheKey]);

  const handleError = () => {
    setStep((s) => {
      const next = s + 1;
      if (next >= urls.length) {
        // All URLs failed; clear cache so next mount retries from start.
        delete logoCache[cacheKey];
        return next;
      }
      return next;
    });
  };

  const handleLoad = () => {
    // Remember which URL worked so next mount starts here directly.
    logoCache[cacheKey] = step;
  };

  // No org key yet (e.g. session still loading): render placeholder instead of
  // requesting `/api/org//logo/...`.
  if (!org) {
    return <GridViewRoundedIcon className={className} style={style} sx={sx} />;
  }

  if (step < urls.length) {
    return (
      <Box
        component="img"
        src={urls[step]}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        className={className}
        style={style}
        sx={sx}
        {...rest}
      />
    );
  }

  return <GridViewRoundedIcon className={className} style={style} sx={sx} />;
};

export default OrgLogo;
